"use client";

/**
 * Asks for a US mobile number when the server refuses a deposit or pack
 * purchase for want of one.
 *
 * Card testers cycle accounts because email addresses are free and unlimited.
 * A mobile number is not, and one number can back only one account for the
 * lifetime of that number, so this is what makes the next account cost an
 * attacker a real SIM. Browsing, withdrawals and PSA transfers never need it,
 * so the dialog opens only on the server's PHONE_VERIFICATION_REQUIRED answer
 * (announced by fetchWithAuth) and can be dismissed. The server enforces.
 */
import { useEffect, useRef, useState } from "react";
import { Button, ErrorMessage } from "@mot-cards/ui";
import {
  AUTH_SESSION_CHANGED_EVENT,
  confirmPhoneVerification,
  fetchWithAuth,
  isAuthenticated,
  PHONE_VERIFICATION_REQUIRED_EVENT,
  startPhoneVerification,
} from "@/lib/auth";

type Step = "number" | "code";

export function PhoneVerificationGate() {
  const [needed, setNeeded] = useState(false);
  const [requiredForOnboarding, setRequiredForOnboarding] = useState(false);
  const [step, setStep] = useState<Step>("number");
  const [phoneInput, setPhoneInput] = useState("");
  /** The server-normalized E.164 value, which confirm must echo back exactly. */
  const [normalized, setNormalized] = useState("");
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  /** Mirrors requiredForOnboarding for the window listeners set up once. */
  const mandatoryRef = useRef(false);
  /** Latest status check; older responses are ignored. */
  const checkIdRef = useRef(0);
  /** Bumped on every reset so in-flight submits from a previous identity are dropped. */
  const sessionRef = useRef(0);

  function setMandatory(value: boolean) {
    mandatoryRef.current = value;
    setRequiredForOnboarding(value);
  }

  function reset() {
    sessionRef.current++;
    checkIdRef.current++;
    setNeeded(false);
    setMandatory(false);
    setStep("number");
    setPhoneInput("");
    setNormalized("");
    setCode("");
    setError("");
  }

  useEffect(() => {
    // A purchase/deposit rejection never downgrades an open mandatory gate.
    const open = () => setNeeded(true);
    const checkAfterSignIn = async () => {
      const id = ++checkIdRef.current;
      if (!isAuthenticated()) return;
      try {
        // A background check must never bounce a stale session to login.
        const res = await fetchWithAuth("/api/v1/me/phone", { onUnauthorized: "throw" });
        if (!res.ok) return;
        const body = await res.json().catch(() => ({}));
        if (id !== checkIdRef.current || !isAuthenticated()) return;
        // Only new accounts must verify to finish signup; legacy accounts are
        // asked when a deposit or pack purchase is refused.
        const required = (body?.data as { requiredAtSignup?: boolean } | undefined)
          ?.requiredAtSignup;
        if (required === true) {
          setMandatory(true);
          setNeeded(true);
        } else if (mandatoryRef.current) {
          // Verified elsewhere (e.g. another tab): release the mandatory gate.
          setMandatory(false);
          setNeeded(false);
        }
      } catch {
        // Do not lock out a newly signed-in customer if the status endpoint is
        // unavailable. The server keeps every phone-protected action denied.
      }
    };
    // A different identity starts clean: never carry one account's gate or
    // phone number over to the next account signed in on this device.
    const onAuthSessionChanged = () => {
      reset();
      void checkAfterSignIn();
    };
    // Sign-in in another tab updates shared localStorage; re-check here too.
    const onStorage = (event: StorageEvent) => {
      if (event.key === null || event.key === "mot_cards_user") onAuthSessionChanged();
    };
    // Verifying in another tab changes nothing locally; re-check on return.
    const onFocus = () => {
      if (mandatoryRef.current) void checkAfterSignIn();
    };
    window.addEventListener(PHONE_VERIFICATION_REQUIRED_EVENT, open);
    window.addEventListener(AUTH_SESSION_CHANGED_EVENT, onAuthSessionChanged);
    window.addEventListener("storage", onStorage);
    window.addEventListener("focus", onFocus);
    // Login navigates to a fresh document, so check on every mount as well.
    void checkAfterSignIn();
    return () => {
      window.removeEventListener(PHONE_VERIFICATION_REQUIRED_EVENT, open);
      window.removeEventListener(AUTH_SESSION_CHANGED_EVENT, onAuthSessionChanged);
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("focus", onFocus);
      checkIdRef.current++;
    };
  }, []);

  async function submitNumber(event: React.FormEvent) {
    event.preventDefault();
    const trimmed = phoneInput.trim();
    if (!trimmed) {
      setError("Enter your mobile number.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      const session = sessionRef.current;
      const e164 = await startPhoneVerification(trimmed);
      if (session !== sessionRef.current) return;
      setNormalized(e164);
      setStep("code");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not send a code to that number.");
    } finally {
      setBusy(false);
    }
  }

  async function submitCode(event: React.FormEvent) {
    event.preventDefault();
    const trimmed = code.trim();
    if (!trimmed) {
      setError("Enter the code from the text message.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      const session = sessionRef.current;
      await confirmPhoneVerification(normalized, trimmed);
      // A different account signed in meanwhile: leave its gate alone.
      if (session !== sessionRef.current) return;
      reset();
    } catch (err) {
      setError(err instanceof Error ? err.message : "That code is incorrect or expired.");
    } finally {
      setBusy(false);
    }
  }

  function editNumber() {
    setStep("number");
    setCode("");
    setError("");
  }

  if (!needed) return null;

  return (
    <div
      className="fixed inset-0 z-[700] flex items-center justify-center bg-[#05080a]/85 p-4 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-labelledby="phone-gate-title"
    >
      <form
        onSubmit={step === "number" ? submitNumber : submitCode}
        className="relative w-full max-w-md rounded-2xl border border-white/10 bg-[#081420] p-6 shadow-2xl"
      >
        <h2
          id="phone-gate-title"
          className="font-[family-name:var(--font-archivo-black)] text-lg font-normal uppercase tracking-[-0.02em] text-white"
        >
          {step === "number" ? "Verify your phone" : "Enter your code"}
        </h2>
        {!requiredForOnboarding && (
          <button
            type="button"
            onClick={() => setNeeded(false)}
            aria-label="Close"
            className="absolute right-4 top-4 text-mot-grey hover:text-white"
          >
            &times;
          </button>
        )}

        {step === "number" ? (
          <p className="mt-2 text-sm leading-snug text-mot-grey">
            {requiredForOnboarding
              ? "Verify your phone to finish creating your account."
              : "Adding funds and buying packs need a verified phone."}{" "}
            We text a code to confirm
            you&rsquo;re a real person. Use a US mobile number — it links
            to this account permanently and can&rsquo;t be reused on another one.
          </p>
        ) : (
          <p className="mt-2 text-sm leading-snug text-mot-grey">
            We sent a code to {normalized}.{" "}
            <button
              type="button"
              onClick={editNumber}
              className="underline underline-offset-2 hover:text-white"
            >
              Use a different number
            </button>
          </p>
        )}

        <ErrorMessage id="phone-gate-error" className="mt-3 rounded-lg bg-mot-danger/10 px-4 py-2">
          {error}
        </ErrorMessage>

        {step === "number" ? (
          <label className="mt-3 flex flex-col gap-1.5">
            <span className="text-xs font-semibold uppercase tracking-widest text-mot-grey">
              Mobile number
            </span>
            <input
              autoFocus
              type="tel"
              inputMode="tel"
              autoComplete="tel-national"
              maxLength={25}
              value={phoneInput}
              onChange={(event) => setPhoneInput(event.target.value)}
              aria-describedby={error ? "phone-gate-error" : undefined}
              placeholder="(555) 010-1234"
              className="rounded-xl border border-white/15 bg-[#050d15] px-4 py-3 text-sm text-white placeholder:text-mot-grey focus-visible:border-mot-teal focus-visible:outline-2 focus-visible:outline-offset-0 focus-visible:outline-mot-teal"
            />
          </label>
        ) : (
          <label className="mt-3 flex flex-col gap-1.5">
            <span className="text-xs font-semibold uppercase tracking-widest text-mot-grey">
              Verification code
            </span>
            <input
              autoFocus
              type="text"
              inputMode="numeric"
              // Lets iOS and Android fill the code straight from the SMS.
              autoComplete="one-time-code"
              maxLength={10}
              value={code}
              onChange={(event) => setCode(event.target.value)}
              aria-describedby={error ? "phone-gate-error" : undefined}
              placeholder="123456"
              className="rounded-xl border border-white/15 bg-[#050d15] px-4 py-3 text-sm tracking-[0.3em] text-white placeholder:tracking-normal placeholder:text-mot-grey focus-visible:border-mot-teal focus-visible:outline-2 focus-visible:outline-offset-0 focus-visible:outline-mot-teal"
            />
          </label>
        )}

        <Button type="submit" variant="primary" disabled={busy} className="mt-4 w-full">
          {busy
            ? step === "number"
              ? "Sending…"
              : "Verifying…"
            : step === "number"
              ? "Text me a code"
              : "Verify"}
        </Button>
      </form>
    </div>
  );
}
